/* eslint-disable react/no-array-index-key, no-nested-ternary */
import React, { useCallback, useState, useRef } from "react";
import _ from "lodash";
import { useQuery } from "@apollo/client";
import { FieldProps } from "@rjsf/core";
import { FormMessage, Select } from "@xometry/xometry_loft";
import lookup from "country-code-lookup";

import {
  GetUserByIdQuery,
  GetUserByIdQueryResult,
  GetUserByIdQueryVariables,
  UserAddress,
  UserDataFragment,
} from "../../../schema/generated/schema";
import { FormContext } from "../types";
import CustomerContactInformation from "../../proposals/CustomerContactInformation";
import { AddAddressLink, SelectStyle } from "./LoftUserAddress.styles";
import { LabelWithOptionalFlag } from "./LabelWithOptionalFlag";
import { Section } from "./Shared.style";
import { emptyAddressObject } from "../utils";

type MutableUserAddress = {
  -readonly [K in keyof UserAddress]: UserAddress[K];
};

export type AdressFormat = Partial<MutableUserAddress> | undefined;
type OptionsFormat = { value: string; label: string }[];
type ShippingAddresses = UserDataFragment["shippingAddresses"];

const formatUserAddress = address => {
  const formattedAddress: AdressFormat = _.pick(address, [
    "name",
    "street1",
    "street2",
    "city",
    "state",
    "zipcode",
    "country",
    "phoneNumber",
    "emailAddress",
  ]);
  const country = lookup.byCountry(address.country || "");
  if (country) {
    formattedAddress.country = country.iso2;
  }
  return formattedAddress;
};

export const formatAddressToLabel = (item: UserAddress | AdressFormat) => {
  if (!item || _.isEmpty(item) || _.isEqual(item, emptyAddressObject)) {
    return "";
  }
  const { state, city, zipcode, street1 } = item;
  return `${street1}, ${city}, ${state} ${zipcode}`;
};

const useParsedState = (
  addresses: ShippingAddresses,
  formData?: AdressFormat
): [string, OptionsFormat] => {
  const parsedOptions = addresses.map((item, index) => {
    const value = `${index}`;
    const label = formatAddressToLabel(item);
    return { label, value };
  });
  const noneOption = { value: "-1", label: "None" };
  parsedOptions.unshift(noneOption);

  if (
    !formData ||
    _.isEmpty(formData) ||
    _.isEqual(formData, emptyAddressObject)
  ) {
    return ["-1", parsedOptions];
  }
  const formDataAOptionIndex = addresses.findIndex(address => {
    const formattedAddress = formatUserAddress(address);
    return _.isEqual(formattedAddress, formData);
  });
  if (formDataAOptionIndex === -1) {
    const value = `${parsedOptions.length}`;
    const label = formatAddressToLabel(formData);
    parsedOptions.push({ value, label });
    return [value, parsedOptions];
  }
  const value = `${formDataAOptionIndex}`;
  return [value, parsedOptions];
};

export const LoftUserAddress = ({
  onChange,
  formContext,
  formData,
  required,
  schema,
  uiSchema,
  rawErrors,
}: FieldProps<AdressFormat>) => {
  const [isContactInformationVisible, setContactInformationVisible] =
    useState(false);
  const isPristineState = useRef(true);

  const { canEdit, isSeller, buyerUserId, touchAllErrors } =
    formContext as FormContext;

  const showErrorList = touchAllErrors || !isPristineState.current;

  const { data: userData } = useQuery<
    GetUserByIdQueryResult,
    GetUserByIdQueryVariables
  >(GetUserByIdQuery, {
    fetchPolicy: "cache-first",
    skip: !buyerUserId,
    variables: { userId: buyerUserId || "" },
    notifyOnNetworkStatusChange: true,
  });

  const addresses = userData?.userById.shippingAddresses || [];

  const [parsedOptionValue, parsedOptions] = useParsedState(
    addresses,
    formData
  );

  const handleOpenForm = useCallback(() => {
    setContactInformationVisible(true);
  }, []);

  const handleChange = (option: string) => {
    isPristineState.current = false;
    const index = parseInt(option, 10);
    const address = addresses[index];
    if (address) {
      const selectedAddress = formatUserAddress(address);
      onChange(selectedAddress);
    } else {
      onChange(undefined);
    }
  };

  const hasOptions = addresses.length > 0;
  const isInvalid = showErrorList && rawErrors?.length > 0;

  return (
    <>
      <div css={Section}>
        <LabelWithOptionalFlag
          required={required}
          tooltip={(uiSchema?.["ui:options"]?.tooltip || "") as string}
        >
          {schema.title}
        </LabelWithOptionalFlag>
        {hasOptions ? (
          <>
            <Select
              disabled={isSeller}
              value={parsedOptionValue}
              invalid={isInvalid}
              onChange={e => {
                if (!isSeller) {
                  handleChange(e.currentTarget.value);
                }
              }}
              css={SelectStyle}
            >
              {parsedOptions.map((opt, idx) => {
                const { label, value } = opt;
                return (
                  <option key={value + idx} value={value}>
                    {label}
                  </option>
                );
              })}
            </Select>
            <AddAddressLink hidden={isSeller}>
              Don&apos;t see the address you&apos;re looking for?
              <b onClick={handleOpenForm} role="button" tabIndex={0}>
                {" "}
                Add a new address to your account.
              </b>
            </AddAddressLink>
          </>
        ) : isSeller ? (
          <Select disabled value="1" css={SelectStyle}>
            <option value="1">{formatAddressToLabel(formData)}</option>
          </Select>
        ) : (
          <AddAddressLink>
            No address is available in your account.
            <b onClick={handleOpenForm} role="button" tabIndex={0}>
              {" "}
              Add a new address to your account.
            </b>
          </AddAddressLink>
        )}
        {isInvalid && (
          <FormMessage invalid>{rawErrors ? rawErrors[0] : ""}</FormMessage>
        )}
      </div>
      <CustomerContactInformation
        open={isContactInformationVisible}
        onCancel={() => setContactInformationVisible(false)}
        onSubmit={newAddress => {
          onChange(formatUserAddress(newAddress));
          setContactInformationVisible(false);
        }}
        onError={() => console.log("Error!")}
        isSeller={isSeller}
        canEdit={canEdit}
        userId={buyerUserId as string}
      />
      {uiSchema?.description && (
        <FormMessage>{uiSchema.description}</FormMessage>
      )}
    </>
  );
};
